/**
 * Data Management Tab - SKU Master Sync Only
 * Product management moved to StockPulse
 */

import React, { useState } from 'react';
import { Zap, Loader2, CheckCircle, RefreshCw, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../../i18n';
import { apiSyncSKUMaster } from '../../../services/api';
import { logger } from '../../../utils/logger';

interface DataManagementTabProps {
  onStatsChange?: () => void;
}

const DataManagementTab: React.FC<DataManagementTabProps> = () => {
  const { language } = useLanguage();

  // SKU Master Sync state
  const [isSyncingSKUMaster, setIsSyncingSKUMaster] = useState(false);
  const [skuMasterSyncResult, setSkuMasterSyncResult] = useState<{
    updated: number;
    inserted: number;
    total: number;
    withCost: number;
    withSize: number;
  } | null>(null);

  const handleSyncSKUMaster = async () => {
    setIsSyncingSKUMaster(true);
    setSkuMasterSyncResult(null);
    try {
      const result = await apiSyncSKUMaster();
      setSkuMasterSyncResult(result);
    } catch (error: unknown) {
      logger.error('SKU Master sync failed:', error);
      const message = error instanceof Error ? error.message : String(error);
      alert(language === 'tr'
        ? `Sync hatası: ${message}`
        : `Sync error: ${message}`);
    } finally {
      setIsSyncingSKUMaster(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-semibold text-blue-900 mb-1">
              Product Management Moved to StockPulse
            </h3>
            <p className="text-sm text-blue-700">
              All product management features (import, export, backup, clear, reset) are now centralized in StockPulse.
              Only SKU Master Sync remains here for marketplace mappings.
            </p>
          </div>
        </div>
      </div>

      {/* Amazon Analyzer SKU Sync */}
      <div className="pt-6 border-t border-slate-200">
        <h4 className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-3">
          <Zap className="w-4 h-4 text-amber-500" />
          {language === 'tr' ? 'Amazon Analyzer SKU Sync' : 'Amazon Analyzer SKU Sync'}
        </h4>
        <div className="flex items-start gap-4">
          <button
            onClick={handleSyncSKUMaster}
            disabled={isSyncingSKUMaster}
            className="flex items-center gap-3 p-4 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors text-left border border-amber-200"
          >
            {isSyncingSKUMaster ? (
              <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
            ) : (
              <RefreshCw className="w-8 h-8 text-amber-600" />
            )}
            <div>
              <div className="font-medium text-slate-800">
                {isSyncingSKUMaster
                  ? (language === 'tr' ? 'Sync ediliyor...' : 'Syncing...')
                  : (language === 'tr' ? 'SKU Master Sync' : 'SKU Master Sync')}
              </div>
              <div className="text-sm text-slate-500">
                {language === 'tr'
                  ? 'Ürün verilerini Amazon Analyzer\'a aktar'
                  : 'Sync product data to Amazon Analyzer'}
              </div>
            </div>
          </button>

          {/* Sync Result */}
          {skuMasterSyncResult && (
            <div className="flex-1 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                <CheckCircle className="w-5 h-5" />
                {language === 'tr' ? 'Sync tamamlandı!' : 'Sync complete!'}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                <div>
                  <div className="text-slate-500">{language === 'tr' ? 'Güncellenen' : 'Updated'}</div>
                  <div className="font-semibold text-slate-800">{skuMasterSyncResult.updated}</div>
                </div>
                <div>
                  <div className="text-slate-500">{language === 'tr' ? 'Yeni Eklenen' : 'Inserted'}</div>
                  <div className="font-semibold text-slate-800">{skuMasterSyncResult.inserted}</div>
                </div>
                <div>
                  <div className="text-slate-500">{language === 'tr' ? 'Toplam SKU' : 'Total SKUs'}</div>
                  <div className="font-semibold text-slate-800">{skuMasterSyncResult.total.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-slate-500">{language === 'tr' ? 'Maliyetli' : 'With Cost'}</div>
                  <div className="font-semibold text-slate-800">{skuMasterSyncResult.withCost.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-slate-500">{language === 'tr' ? 'Desi\'li' : 'With Size'}</div>
                  <div className="font-semibold text-slate-800">{skuMasterSyncResult.withSize.toLocaleString()}</div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="mt-3 text-xs text-slate-500">
          {language === 'tr'
            ? 'StockPulse\'de ürün bilgilerini güncelledikten sonra bu butona tıklayarak Amazon Analyzer\'ın kullandığı SKU Master tablosunu güncelleyebilirsiniz.'
            : 'After updating product info in StockPulse, click this button to sync the SKU Master table used by Amazon Analyzer.'}
        </div>
      </div>
    </div>
  );
};

export default DataManagementTab;
